import React from 'react';
import { motion } from 'framer-motion';
import { Settings, CheckCircle, Search, Wrench, BatteryCharging } from 'lucide-react';

const InstallationPage = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    {
      icon: Search,
      title: 'Site Inspection & Evaluation',
      desc: 'Once your order is placed, a trained technician will visit your property to evaluate your roof conditions, shadow-free area, and existing electrical setup.'
    },
    {
      icon: Settings,
      title: 'Design & Permitting',
      desc: 'Our engineers design a custom solar layout maximizing efficiency. We handle all net-metering approvals and DISCOM (utility) permitting required by the government.'
    },
    {
      icon: Wrench,
      title: 'System Installation',
      desc: 'The verified vendor team arrives with your panels, inverters, and mounting structures. The mounting structures are secured first, followed by the panels and wiring.'
    },
    {
      icon: CheckCircle,
      title: 'Testing & Commissioning',
      desc: 'After installation, the system is tested for safety and efficiency. Once the utility installs the net-meter, your system is officially "turned on".'
    },
    {
      icon: BatteryCharging,
      title: 'Handover & App Setup',
      desc: 'You will receive all warranties, manuals, and a complete walkthrough. We also help you set up the monitoring app so you can track your generation in real-time.'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow pt-24 bg-background">
        <div className="container-solar py-12 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              The <span className="solar-text-gradient">Installation</span> Process
            </h1>
            <p className="text-lg text-muted-foreground">
              Transitioning to solar energy with SolarSavers is hassle-free. From placing your order to turning on the power, we guide you through every step.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto relative border-l-2 border-primary/20 pl-8 ml-4 md:mx-auto md:border-none md:pl-0">
            <div className="hidden md:block absolute left-1/2 top-4 bottom-4 w-1 bg-primary/20 -translate-x-1/2 rounded-full"></div>
            
            <div className="space-y-12">
              {steps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group`}
                >
                  <div className="hidden md:block w-1/2"></div>
                  
                  <div className="absolute -left-[41px] md:left-1/2 md:-translate-x-1/2 bg-background border-4 border-card p-3 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform z-10 text-primary">
                    <step.icon className="w-6 h-6" />
                  </div>

                  <div className="w-full md:w-5/12 bg-card p-6 rounded-2xl shadow-sm border border-border group-hover:border-primary/50 transition-colors">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                       <span className="text-primary font-black opacity-50">0{i+1}.</span> {step.title}
                    </h3>
                    <p className="text-muted-foreground">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InstallationPage;
